import classNames from 'clsx';
import { xray } from 'lib/xray';
import debounce from 'lodash.debounce';
import React, { type ReactElement, memo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { IS_DEV } from 'reactApp/appHelpers/configHelpers';
import { tariffBuyCustomTitle } from 'reactApp/appHelpers/featuresHelpers';
import { disableAdsModalResizeWait } from 'reactApp/appHelpers/featuresHelpers/features/disableAdsModalResize';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { initProducts } from 'reactApp/modules/products/products.module';
import type { ResizeParams } from 'reactApp/ui/IframePages/BuyDisableAds/BuyDisableAds.types';
import { DisableAdsPostMessageType, sendFrameMessage } from 'reactApp/ui/IframePages/BuyDisableAds/utils';
import { BuyFamilyTariff } from 'reactApp/ui/IframePages/BuyFamilyTariff/BuyFamilyTariff';
import { isFramed } from 'reactApp/utils/isFramed';
import opener from 'reactApp/utils/opener';

import styles from './BuyDisableAds.css';
import { DisableAdsScreen } from './DisableAdsScreen/DisableAdsScreen';
import { useEmbeddedBuyScreen } from './hooks/useEmbeddedBuyScreen';

interface BuyIframeQueryParams {
    title?: string;
    tariffs?: string;
    activeTab?: 'y' | 'm';
    fastcheckoutTitle?: string;
    isFamilyTariff?: boolean;
    analyticsMap?: string;
}

const {
    title: titleFromQuery,
    tariffs: tariffsFromQuery,
    activeTab: activeTabFromQuery,
    fastcheckoutTitle: fastcheckoutTitleFromQuery,
    isFamilyTariff: isFamilyTariffFromQuery,
    analyticsMap: analyticsFromQuery,
}: BuyIframeQueryParams = getQueryParams();

export const BuyDisableAdsIframe = memo((): ReactElement => {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(initProducts());
    }, []);

    if (!isFramed() && !IS_DEV) {
        xray.send(`buy-disable-ads-frame_error_not_frame`);
        opener('/', true);
    }

    const fastcheckoutTitle = isFamilyTariffFromQuery
        ? 'Семейная подписка Mail Space'
        : fastcheckoutTitleFromQuery
        ? tariffBuyCustomTitle[fastcheckoutTitleFromQuery]
        : undefined;

    const onResize = useCallback(
        debounce(({ height }: ResizeParams) => {
            sendFrameMessage({ type: DisableAdsPostMessageType.size, height, width: BREAKPOINT_SM });
        }, disableAdsModalResizeWait),
        []
    );

    const { embeddedContent, openEmbeddedBuy } = useEmbeddedBuyScreen(fastcheckoutTitle, analyticsFromQuery, tariffsFromQuery, onResize);

    if (isFamilyTariffFromQuery) {
        return (
            <div
                className={classNames(styles.root, {
                    [styles.familyRoot]: isFamilyTariffFromQuery,
                })}
            >
                <BuyFamilyTariff onBuyClick={openEmbeddedBuy} />
                {embeddedContent}
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <DisableAdsScreen
                titleFromQuery={titleFromQuery}
                tariffsIdsFromQuery={tariffsFromQuery}
                activeTabFromQuery={activeTabFromQuery}
                onBuyClick={openEmbeddedBuy}
                analyticsFromQuery={analyticsFromQuery}
                onResize={onResize}
            />
            {embeddedContent}
        </div>
    );
});

BuyDisableAdsIframe.displayName = 'BuyDisableAdsIframe';
